import clsx from 'clsx';
import styles from './email-form-otp-step.module.scss';
import { Button, Typography } from '@/ui-kit';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ErrorNotificationIcon, FillProfileIcon } from '@/assets/icons';
import { padStart } from 'lodash';
import otpImg from '@/assets/images/otp.png';
import { signInOtpSchema, SignInOtpType } from '@/schemes/sign-in-otp-schema';
import { AxiosError, AxiosResponse } from 'axios';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { ButtonTextColor } from '@/ui-kit/button';
import { VerifyOtpResponse } from '@/api/auth';
import { SignUpActions } from '../sign-up-actions';
import { OtpInput } from '../otp-input';

interface Props {
    secondsBeforeOTPRequest: number | null
    email: string
    error: string | null
    useGradient?: boolean
    className?: string
    nextBtnStyles?: string
    disabledNextBtnStyles?: string
    nextBtnTextColor?: ButtonTextColor
    setError: Dispatch<SetStateAction<string | null>>
    returnToEmailInput: () => void
    showErrorToast: () => void
    resendCode: () => Promise<void>
    verifyOTP: (email: string, code: string) => Promise<AxiosResponse>
    onLogin: (data: VerifyOtpResponse) => void
}

export const EmailFormOtpStep: React.FC<Props> = ({
    secondsBeforeOTPRequest,
    email,
    error,
    className,
    nextBtnStyles = '',
    disabledNextBtnStyles = '',
    nextBtnTextColor = 'white',
    showErrorToast,
    setError,
    returnToEmailInput,
    resendCode,
    verifyOTP,
    onLogin,
}) => {
    const [isLoading, setIsLoading] = useState(false)

    const { t } = useTranslation()

    const enterOtpInitialValues = useMemo(() => {
        return {
            otpFirstChar: '',
            otpSecondChar: '',
            otpThirdChar: '',
            otpFourthChar: '',
        }
    }, [])
    
    const enterOTPMethods = useForm<SignInOtpType>({
        resolver: zodResolver(signInOtpSchema),
        defaultValues: enterOtpInitialValues
    });
    
    const {
        register: enterOTPRegister,
        handleSubmit,
        formState: { isValid },
    } = enterOTPMethods
    
    const onOtpInput = async (e: React.FormEvent<HTMLInputElement>, nextEl?: HTMLInputElement | null) => {
        if (e.currentTarget.value.length) {
            const nextEl = e.currentTarget.nextElementSibling
            const isNotLastFilledInput =
                nextEl && e.currentTarget.value.length
            if (
                isNotLastFilledInput &&
                nextEl instanceof HTMLInputElement
            ) nextEl.focus()
            if (!nextEl) e.currentTarget.blur()
        }
    }

    const displayError = (error: unknown) => {
        if (error instanceof AxiosError) {
            const res = error.response
            const status = res?.status
            const data = res?.data
            if (status === 401 || status === 422)
                return setError(data.error)
        }
        showErrorToast()
    }

    const onSubmit = async () => {
        setError(null)
        try {
            setIsLoading(true);
            const res = await verifyOTP(
                email,
                [
                    enterOTPMethods.getValues('otpFirstChar'),
                    enterOTPMethods.getValues('otpSecondChar'),
                    enterOTPMethods.getValues('otpThirdChar'),
                    enterOTPMethods.getValues('otpFourthChar'),
                ].join('')
            )
            if (res) onLogin(res.data)
        } catch (error) {
            displayError(error)
            enterOTPMethods.reset()
        } finally {
            setIsLoading(false)
        }
    }

    const onReturnToEmailInput = () => {
        if (isLoading) return;
        returnToEmailInput()
    }

    const onResendClick = async () => {
        setIsLoading(true)
        await resendCode()
        setIsLoading(false)
    }

    const fillZeroes = (val: string) => padStart(val, 2, '0')

    const displayTimeout = () => {
        if (!secondsBeforeOTPRequest) return '00:00'
        const mins = Math.floor(secondsBeforeOTPRequest / 60)
        return `${fillZeroes(mins.toString())}:${fillZeroes((secondsBeforeOTPRequest - mins * 60).toString())}`
    }
    
    useEffect(() => {
        callYaMetrika('code_screen_loaded')
    }, [])

    useEffect(() => {
        if (isValid) {
            const currentEl = document.activeElement
            if (currentEl instanceof HTMLInputElement)
                currentEl.blur()
            onSubmit()
        }
    }, [isValid])

    const INPUT_MAX_LEN = 1

    const otpFirstInputRegister = enterOTPRegister('otpFirstChar')
    const otpSecondInputRegister = enterOTPRegister('otpSecondChar')
    const otpThirdInputRegister = enterOTPRegister('otpThirdChar')
    const otpFourthInputRegister = enterOTPRegister('otpFourthChar')
    
    return (
        <FormProvider { ...enterOTPMethods }>
            <form
                className={
                    clsx(
                        styles.wrapper,
                        className
                    )
                }
                onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.content}>
                    <img className={styles.otpImg} src={otpImg} />
                    <div className={styles.emailInfo}>
                        <Typography
                            variant="span"
                            fontWeight="medium"
                            className={clsx(styles.titleForm, styles.otpTitleForm)}
                            center>
                            {t('auth.otp_sent_to', { email })}
                        </Typography>
                        <FillProfileIcon
                            width={16}
                            height={16}
                            className={styles.emailInfoEditBtn}
                            onClick={onReturnToEmailInput} />
                    </div>
                    <Typography
                        variant="p"
                        fontWeight="medium"
                        className={styles.subtitleForm}
                        center>
                        {t('auth.input_for_continue')}
                    </Typography>
                    <div className={styles.otpInputView}>
                        <OtpInput
                            error={error}
                            register={otpFirstInputRegister}
                            onOtpInput={onOtpInput} />
                        <OtpInput
                            error={error}
                            register={otpSecondInputRegister}
                            onOtpInput={onOtpInput} />
                        <OtpInput
                            error={error}
                            register={otpThirdInputRegister}
                            onOtpInput={onOtpInput} />
                        <OtpInput
                            error={error}
                            register={otpFourthInputRegister}
                            onOtpInput={onOtpInput} />
                    </div>
                    {
                        error &&
                        <div className={clsx(styles.error, styles.otpError)}>
                            <ErrorNotificationIcon className={styles.errorIcon} />
                            <Typography className={styles.errorLabel}>
                                {error}
                            </Typography>

                        </div>
                    }
                    <SignUpActions className={styles.actions}>
                        {
                            secondsBeforeOTPRequest ?
                                <Button
                                    textColor="black"
                                    className={styles.resendCodeInfo}
                                    isLoading={isLoading}
                                    isDisabled={isLoading}>{t('auth.send_new_code_timeout', { timeout: displayTimeout() })}</Button>
                            :
                                <Button
                                    type="submit"
                                    variant="stretched"
                                    typeColor="white"
                                    textColor={nextBtnTextColor}
                                    className={
                                        clsx(
                                            styles.submitBtn,
                                            isLoading && styles.submitBtnDisabled,
                                            nextBtnStyles,
                                            isLoading && disabledNextBtnStyles,
                                        )
                                    }
                                    isLoading={isLoading}
                                    isDisabled={isLoading}
                                    onClick={onResendClick}>{t('auth.send_new_code')}</Button>
                        }
                    </SignUpActions>
                </div>
            </form>
        </FormProvider>
    );
}
